/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import {
  FormControl,
  FormLabel,
  Input,
  InputProps,
  Textarea,
  TextareaProps,
  Typography,
} from "@mui/joy";
import React, { useCallback, memo } from "react";

// Character count component (memoized for performance)
const FormCharCount = memo(
  ({ current, max }: { current: number | undefined; max: number }) => (
    <Typography
      level="body-xs"
      sx={{
        textAlign: "right",
        color: (current ?? 0) > max ? "danger.500" : "neutral.500",
        fontSize: "0.875rem",
      }}
    >
      {current ?? 0}/{max}
    </Typography>
  )
);
FormCharCount.displayName = "FormCharCount";

// Common props for both input types
interface CommonProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  id?: string;
}

// Props specific to Input component
interface TextInputProps extends CommonProps {
  variant: "input";
  inputProps?: Omit<InputProps, "value" | "onChange" | "placeholder">;
}

// Props specific to Textarea component
interface TextareaInputProps extends CommonProps {
  variant: "textarea";
  minRows?: number;
  textareaProps?: Omit<TextareaProps, "value" | "onChange" | "placeholder" | "minRows">;
}

// Union type for the component props
type CharacterCountInputProps = TextInputProps | TextareaInputProps;

export function CharacterCountInput(props: CharacterCountInputProps): JSX.Element {
  const {
    label,
    value,
    onChange,
    maxLength,
    placeholder,
    required,
    error,
    errorMessage,
    id,
  } = props;

  // Handle change events
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // Common styles for input elements
  const commonInputStyles = {
    width: "100%",
    fontSize: "1rem",
    "&::placeholder": {
      fontSize: "1rem",
    },
  };

  return (
    <FormControl required={required} error={error}>
      {label && (
        <FormLabel htmlFor={id} sx={{ fontSize: "1rem", mb: props.variant === "textarea" ? 1 : undefined }}>
          {label}
        </FormLabel>
      )}

      {props.variant === "input" ? (
        <Input
          id={id}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          sx={{ width: "100%" }}
          slotProps={{
            input: {
              sx: commonInputStyles,
            },
          }}
          {...props.inputProps}
        />
      ) : (
        <Textarea
          id={id}
          minRows={props.minRows || 3}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          sx={{ width: "100%" }}
          slotProps={{
            textarea: {
              sx: commonInputStyles,
            },
          }}
          {...props.textareaProps}
        />
      )}

      <FormCharCount current={value?.length} max={maxLength} />
      
      {error && errorMessage && (
        <Typography level="body-xs" color="danger">
          {errorMessage}
        </Typography>
      )}
    </FormControl>
  );
}
